import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Target, CheckCircle, Info } from "lucide-react";
import BasicAdvancedWrapper, {
  BasicAdvancedWrapperProps,
} from "../components/BasicAdvancedWrapper";
import { MultiStepConfig } from "../components/MultiStepGradeModal";
import { CreateModalProps } from "../index";
import {
  ContextAndInfoStep,
  AssignmentInstructionsStep,
  CriteriaAndPointStep,
  AssessmentConfigStep,
} from "./RubricAdvancedSteps";
import {
  RubricGeneratorCreateRequestDto,
  RubricGeneratorCreateRequestDtoRubricType,
} from "@packages/api/madlenAPI.schemas";
import RubricSpecificFields from "./RubricSpecificFields";

interface RubricCreateModalMultiStepProps
  extends Omit<CreateModalProps, "onSubmit"> {
  onSubmit: (input: RubricGeneratorCreateRequestDto) => Promise<void>;
}

export const RubricCreateModal: React.FC<RubricCreateModalMultiStepProps> = ({
  onSubmit,
  initialInput,
  ...props
}) => {
  const { t } = useTranslation();

  // State for basic rubric fields
  const [basicRubricData, setBasicRubricData] = useState<{
    rubricType: RubricGeneratorCreateRequestDtoRubricType;
    totalScore: number;
    assignmentInstructions: string;
  }>({
    rubricType: RubricGeneratorCreateRequestDtoRubricType.ANALYTIC,
    totalScore: 70,
    assignmentInstructions: "",
  });

  // Define the initial data structure for advanced mode
  const initialAdvancedData: RubricGeneratorCreateRequestDto = {
    // Basic CreateModal data (will be populated from first step)
    courseName: initialInput.courseName as any,
    gradeLevel: initialInput.gradeLevel,
    prompt: initialInput.prompt,
    environment: initialInput.environment,
    isPublic: initialInput.isPublic,
    generationType: initialInput.generationType as any,
    teacherDocumentIds: initialInput.teacherDocumentIds || [],
    learningOutcomeIds: initialInput.learningOutcomeIds || [],
    contentLanguage: initialInput.outputLanguage as any,

    // Context and Additional Info defaults
    contextOfUse: "",

    // Advanced rubric-specific defaults
    rubricType: RubricGeneratorCreateRequestDtoRubricType.ANALYTIC,
    totalScore: 70,
    assignmentInstructions: "",

    // Advanced configuration defaults
    performanceLevelNames: [],
    levels: 4,
  };

  // Define the advanced steps configuration
  const advancedSteps: MultiStepConfig<RubricGeneratorCreateRequestDto>[] = [
    {
      id: "context-info",
      title: t("rubric.steps.context.title", "Context & Info"),
      description: t(
        "rubric.steps.context.description",
        "Provide relevant context information",
      ),
      icon: <Info size={16} />,
      component: ContextAndInfoStep,
      validation: (data) => {
        // Context step is optional, so always valid
        return true;
      },
    },
    {
      id: "assignment-instructions",
      title: t("rubric.steps.assignment.title", "Ödev Talimatları"),
      description: t(
        "rubric.steps.assignment.description",
        "Ödeviniz hakkında detaylı bilgi verin",
      ),
      icon: <Target size={16} />,
      component: AssignmentInstructionsStep,
      validation: (data) => {
        return !!(
          data.assignmentInstructions &&
          data.assignmentInstructions.trim().length > 0
        );
      },
    },
    {
      id: "custom-criteria",
      title: t("rubric.steps.criteria.title", "Değerlendirme Kriterleri"),
      description: t(
        "rubric.steps.criteria.description",
        "Ödevinizde en önemli olan şeyleri seçin",
      ),
      icon: <Target size={16} />,
      component: CriteriaAndPointStep,
      validation: (data) => {
        // For analytic rubrics, require at least one assessment focus or custom criterion
        if (
          data.rubricType === RubricGeneratorCreateRequestDtoRubricType.ANALYTIC
        ) {
          return !!(
            (data.evaluationCriteria && data.evaluationCriteria.length > 0) ||
            (data.performanceLevelNames &&
              data.performanceLevelNames.length > 0)
          );
        }
        // For holistic rubrics, this step is optional
        return true;
      },
    },
    {
      id: "assessment-config",
      title: t("rubric.steps.assessment.title", "Assessment Config"),
      description: t(
        "rubric.steps.assessment.description",
        "Configure assessment settings",
      ),
      icon: <CheckCircle size={16} />,
      component: AssessmentConfigStep,
      validation: (data) => {
        // All fields in this step are optional, so always valid
        return true;
      },
    },
  ];

  // Handle basic mode submission (original behavior)
  const handleBasicSubmit = async (data: any) => {
    // Add basic rubric-specific fields from component state
    const submissionData = {
      ...data,
      rubricType: basicRubricData.rubricType,
      totalScore: basicRubricData.totalScore,
      assignmentInstructions:
        basicRubricData.assignmentInstructions || data.prompt, // Use assignment instructions if provided, otherwise use prompt
    };

    await onSubmit(submissionData);
  };

  // Handle rubric-specific fields changes
  const handleRubricFieldsChange = useCallback(
    (rubricData: {
      rubricType: RubricGeneratorCreateRequestDtoRubricType;
      totalScore: number;
      assignmentInstructions: string;
    }) => {
      setBasicRubricData(rubricData);
    },
    [],
  );

  // Handle advanced mode submission
  const handleAdvancedSubmit = async (
    data: RubricGeneratorCreateRequestDto,
  ) => {
    await onSubmit(data);
  };

  // Prepare the wrapper props
  const wrapperProps: BasicAdvancedWrapperProps<RubricGeneratorCreateRequestDto> =
    {
      createModalProps: {
        ...props,
        initialInput,
        themeColor: "green",
        onSubmit: handleBasicSubmit, // This will be set by the wrapper
        after: (
          <RubricSpecificFields
            initialData={basicRubricData}
            onChange={handleRubricFieldsChange}
            themeColor="green"
          />
        ),
      },
      multiStepProps: {
        steps: advancedSteps,
        initialData: initialAdvancedData,
        title: t("quickActions.rubricCreate", "Create Rubric"),
        themeColor: "green",
        backPath: "/content/rubric",
        isLoading: props.isLoading,
        error: props.error,
        loadingTitle: props.loadingTitle,
        errorTitle: props.errorTitle,
        errorDescription: props.errorDescription,
        onFinalSubmit: handleAdvancedSubmit,
        basicModeSubmitText: t("common.generate", "Generate"),
        advancedModeSubmitText: t(
          "rubric.createAdvanced",
          "Create Advanced Rubric",
        ),
      },
      themeColor: "green",
      title: t("quickActions.rubricCreate", "Create Rubric"),
      backPath: "/content/rubric",
      enableAdvancedMode: true,
      defaultMode: "basic",
      showModeToggle: true,
      onBasicSubmit: handleBasicSubmit,
      onAdvancedSubmit: handleAdvancedSubmit,
    };

  return <BasicAdvancedWrapper {...wrapperProps} />;
};

export default RubricCreateModal;
